/**
 * Banner Carousel Section
 *
 * Image sizes are managed in Contentful (no frontend validation).
 * - fullWidthAd: for web (desktop ~2880x760, mobile ~750x760)
 * - advertisement: for mobile app only (704x310-450)
 *
 * Mobile images shown at 640px breakpoint or below.
 * Falls back to P1 Hero when carousel has no data.
 */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import Slider from 'react-slick';
import Skeleton from 'react-loading-skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import { createStyles } from 'utils/createStyle';
import { useIsInViewport } from 'hooks/useIsInViewport';
import useMediaQueries from 'hooks/useMediaQueries';
import { useAdCarousel, CarouselBannerItem } from './useAdCarousel';
import { useSlideVisibilityTracking } from './useSlideVisibilityTracking';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const AUTO_PLAY_INTERVAL = 4000;

const styles = createStyles({
    container: [
        'relative overflow-hidden mx-auto',
        {
            margin: '24px 24px 0',
            borderRadius: '20px',
            '@media (min-width: 640px)': {
                margin: '34px 34px 0',
                borderRadius: '40px',
            },
        },
    ],
    slideContainer: ['w-full'],
    imageWrapper: ['block w-full'],
    image: [
        'w-full h-auto',
        {
            borderRadius: '20px',
            '@media (min-width: 640px)': {
                borderRadius: '40px',
            },
        },
    ],
    arrowButton: [
        'absolute top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all cursor-pointer border-0',
        {
            width: 'clamp(32px, 8vw, 44px)',
            height: 'clamp(32px, 8vw, 44px)',
            '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
            '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
            },
        },
    ],
    prevArrow: ['left-4'],
    nextArrow: ['right-4'],
    arrowIcon: [
        {
            color: '#005E9D',
            fontSize: 'clamp(12px, 3vw, 16px)',
        },
    ],
});

interface ArrowProps {
    onClick?: () => void;
    direction: 'prev' | 'next';
    disabled?: boolean;
}

const CustomArrow = ({ onClick, direction, disabled }: ArrowProps) => (
    <button
        className={`${styles.arrowButton} ${
            direction === 'prev' ? styles.prevArrow : styles.nextArrow
        }`}
        onClick={onClick}
        disabled={disabled}
        aria-label={direction === 'prev' ? 'Previous slide' : 'Next slide'}
    >
        <FontAwesomeIcon
            icon={direction === 'prev' ? faChevronLeft : faChevronRight}
            className={styles.arrowIcon}
        />
    </button>
);

interface BannerSlideProps {
    banner: CarouselBannerItem;
    index: number;
    onBannerClick: (banner: CarouselBannerItem, index: number) => Promise<void>;
}

const BannerSlide = ({ banner, index, onBannerClick }: BannerSlideProps) => {
    const [imageUrl, setImageUrl] = useState(banner.imageUrl);

    const queries = useMemo(
        () => ({
            '(max-width: 639px)': () => {
                setImageUrl(banner.mobileImageUrl ?? banner.imageUrl);
            },
            '(min-width: 640px)': () => {
                setImageUrl(banner.imageUrl);
            },
        }),
        [banner.imageUrl, banner.mobileImageUrl]
    );

    useMediaQueries(queries);

    const handleClick = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();
            await onBannerClick(banner, index);
        },
        [banner, index, onBannerClick]
    );

    const imageElement = <img className={styles.image} src={imageUrl} alt={banner.imageAlt} />;

    return (
        <div className={styles.slideContainer}>
            {banner.hasLink ? (
                <a href="#" onClick={handleClick} className={styles.imageWrapper}>
                    {imageElement}
                </a>
            ) : (
                <div className={styles.imageWrapper}>{imageElement}</div>
            )}
        </div>
    );
};

export const BannerCarouselSection = () => {
    const sliderRef = useRef<Slider>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const dragStartPos = useRef<{ x: number; y: number } | null>(null);
    const isInViewport = useIsInViewport(containerRef as React.RefObject<HTMLDivElement>);
    const [userHasInteracted, setUserHasInteracted] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    const { banners, isLoading, handleBannerClick: originalHandleBannerClick } = useAdCarousel();

    // Wrap banner click to prevent navigation after drag
    const handleBannerClick = useCallback(
        async (banner: CarouselBannerItem, index: number) => {
            if (dragStartPos.current) {
                // Was a drag, don't navigate - reset for next interaction
                dragStartPos.current = null;
                return;
            }
            await originalHandleBannerClick(banner, index);
        },
        [originalHandleBannerClick]
    );

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        dragStartPos.current = { x: e.clientX, y: e.clientY };
    }, []);

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        if (dragStartPos.current) {
            const dx = Math.abs(e.clientX - dragStartPos.current.x);
            const dy = Math.abs(e.clientY - dragStartPos.current.y);
            // If moved more than 10px, it was a drag
            if (dx > 10 || dy > 10) {
                // Keep dragStartPos set to block the click
                return;
            }
        }
        // Was a click, not a drag - clear the position
        dragStartPos.current = null;
    }, []);
    const { logSlideIfNotLogged } = useSlideVisibilityTracking(banners, isInViewport);

    const hasMultipleBanners = banners.length > 1;

    // Log first slide when carousel becomes visible
    useEffect(() => {
        if (isInViewport && banners.length > 0) {
            logSlideIfNotLogged(0);
        }
    }, [isInViewport, banners.length, logSlideIfNotLogged]);

    // Manual auto-advance with interval (react-slick's autoplay doesn't respond to dynamic changes)
    useEffect(() => {
        if (!hasMultipleBanners || userHasInteracted) {
            return;
        }

        const interval = setInterval(() => {
            sliderRef.current?.slickNext();
        }, AUTO_PLAY_INTERVAL);

        return () => clearInterval(interval);
    }, [hasMultipleBanners, userHasInteracted]);

    // Handle user interaction - pause auto-advance
    const handleUserInteraction = useCallback(() => {
        if (!userHasInteracted) {
            setUserHasInteracted(true);
        }
    }, [userHasInteracted]);

    // Reset user interaction when page visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setUserHasInteracted(false);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const handlePrev = useCallback(() => {
        handleUserInteraction();
        sliderRef.current?.slickPrev();
    }, [handleUserInteraction]);

    const handleNext = useCallback(() => {
        handleUserInteraction();
        sliderRef.current?.slickNext();
    }, [handleUserInteraction]);

    const handleBeforeChange = useCallback(
        (_oldIndex: number, newIndex: number) => {
            setCurrentSlide(newIndex);
            logSlideIfNotLogged(newIndex);
        },
        [logSlideIfNotLogged]
    );

    const handleSwipe = useCallback(() => {
        handleUserInteraction();
    }, [handleUserInteraction]);

    const settings = useMemo(
        () => ({
            dots: false,
            infinite: hasMultipleBanners,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            autoplay: false,
            arrows: false,
            // draggable: false,
            // swipe: false,
            // touchMove: false,
            beforeChange: handleBeforeChange,
            onSwipe: handleSwipe,
        }),
        [hasMultipleBanners, handleBeforeChange, handleSwipe]
    );

    if (isLoading) {
        return (
            <div className={styles.container}>
                <Skeleton height={300} />
            </div>
        );
    }

    if (banners.length === 0) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className={styles.container}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
            <Slider ref={sliderRef} {...settings}>
                {banners.map((banner, index) => (
                    <BannerSlide
                        key={banner.id}
                        banner={banner}
                        index={index}
                        onBannerClick={handleBannerClick}
                    />
                ))}
            </Slider>

            {hasMultipleBanners && (
                <>
                    <CustomArrow
                        direction="prev"
                        onClick={handlePrev}
                        disabled={!settings.infinite && currentSlide === 0}
                    />
                    <CustomArrow
                        direction="next"
                        onClick={handleNext}
                        disabled={!settings.infinite && currentSlide === banners.length - 1}
                    />
                </>
            )}
        </div>
    );
};
